<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import { onMount } from "svelte";
	import type { HTMLDialogAttributes } from "svelte/elements";
	import cross from "@core/assets/icons/cross.svg?raw";
	import { lockScroll } from "../../state/lockScroll.svelte.js";

	let {
		show = $bindable(false),
		/* eslint-disable prefer-const */
		...rest
		/* eslint-enable prefer-const */
	}: HTMLDialogAttributes & { show: boolean } = $props();

	let dialog = $state<HTMLDialogElement | null>(null);
	let backdrop = $state<HTMLDivElement | null>(null);
	let isLocked = $state(false);
	let isVisible = $state(false);

	$effect(() => {
		if (!dialog) {
			return undefined;
		}

		if (show && !dialog.open) {
			dialog.showModal();
			requestAnimationFrame(() => {
				isVisible = true;
			});
			return undefined;
		}
		if (!show && dialog.open) {
			isVisible = false;
			const d = dialog;
			const timeout = setTimeout(() => {
				d.close();
			}, 200);
			return (): void => {
				clearTimeout(timeout);
			};
		}
		return undefined;
	});

	$effect(() => {
		if (show && !isLocked) {
			lockScroll.lock = true;
			isLocked = true;
		}
		if (!show && isLocked) {
			lockScroll.lock = false;
			isLocked = false;
		}
	});

	onMount(() => {
		return (): void => {
			if (isLocked) {
				lockScroll.lock = false;
				isLocked = false;
			}
		};
	});

	function close(): void {
		show = false;
	}

	function closeOnBackdrop(event: MouseEvent): void {
		if (!event.target) {
			return;
		}

		if (!(event.target instanceof Node)) {
			return;
		}

		if (event.target === dialog || event.target === backdrop) {
			close();
		}
	}

	onMount(() => {
		dialog?.addEventListener("click", closeOnBackdrop);
		return (): void => {
			dialog?.removeEventListener("click", closeOnBackdrop);
		};
	});
</script>

<dialog
	bind:this={dialog}
	{...rest}
	class={[
		"modal-dialog bg-transparent p-0 max-w-full max-h-[100dvh] w-full md:w-auto mx-auto backdrop:bg-midnight/50 backdrop:backdrop-blur-sm backdrop:transition-opacity backdrop:duration-200 overflow-visible",
		isVisible ? "backdrop:opacity-100" : "backdrop:opacity-0",
		rest.class,
	]}
	onclose={(event: Event & { currentTarget: EventTarget & HTMLDialogElement }): void => {
		show = false;
		rest.onclose?.(event);
	}}
	oncancel={(event: Event): void => {
		event.preventDefault();
		close();
	}}
>
	<div bind:this={backdrop} class="flex items-center justify-center min-h-[100dvh] p-4">
		<div
			class="modal-content bg-ivory-100 relative rounded-3xl shadow-xl border border-ivory-200 w-full md:w-auto md:min-w-96 max-h-[calc(100dvh-2rem)] flex flex-col transition-all duration-200 ease-out {isVisible
				? 'opacity-100 scale-100 translate-y-0'
				: 'opacity-0 scale-95 translate-y-4'}"
		>
			<button
				class="text-hazelnut/40 hover:text-hazelnut transition-colors duration-300 absolute top-3 right-3 md:top-4 md:right-4 z-10 p-1"
				onclick={close}
				aria-label="Zavřít"
			>
				<Icon icon={cross} class="w-5" square />
			</button>

			<div class="overflow-y-auto overscroll-contain p-5 md:p-7 rounded-3xl">
				{@render rest.children?.()}
			</div>
		</div>
	</div>
</dialog>

<style>
	.modal-dialog::backdrop {
		transition: opacity 0.2s ease-out;
	}
</style>
